import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../../services/api';
import { toast } from 'react-toastify';

const WhatsAppAdvancedFeatures = () => {
  const [activeTab, setActiveTab] = useState('otp');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({});

  // OTP State
  const [otpPhone, setOtpPhone] = useState('');
  const [otpType, setOtpType] = useState('login');

  // Voice Processing State
  const [voiceFile, setVoiceFile] = useState(null);
  const [voicePhone, setVoicePhone] = useState('');

  // Payment Links State
  const [paymentData, setPaymentData] = useState({
    orderId: '',
    phoneNumber: '',
    amount: '',
    currency: 'INR',
    gateway: 'razorpay'
  });

  // AI Features State
  const [aiText, setAiText] = useState('');
  const [aiPhone, setAiPhone] = useState('');
  const [aiResults, setAiResults] = useState({});

  // Ticketing State
  const [ticketData, setTicketData] = useState({
    phoneNumber: '',
    subject: '',
    description: '',
    ticketType: 'general',
    priority: 'medium'
  });

  // Groups State
  const [groupData, setGroupData] = useState({
    groupName: '',
    description: '',
    groupType: 'community',
    maxMembers: 256
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const [otpStats, voiceStats, paymentStats, aiStats, ticketStats] = await Promise.all([
        whatsappAPI.getOtpAnalytics('7d'),
        whatsappAPI.getVoiceAnalytics('7d'),
        whatsappAPI.getPaymentAnalytics('7d'),
        whatsappAPI.getAiAnalytics('7d'),
        whatsappAPI.getTicketAnalytics('7d')
      ]);

      setStats({
        otp: otpStats.data?.data || [],
        voice: voiceStats.data?.data || [],
        payment: paymentStats.data?.data || [],
        ai: aiStats.data?.data || [],
        tickets: ticketStats.data?.data || []
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // OTP Functions
  const sendTestOTP = async () => {
    if (!otpPhone) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.sendOtp({
        phoneNumber: otpPhone,
        verificationType: otpType
      });

      if (response.data.success) {
        toast.success('Test OTP sent successfully!');
      } else {
        toast.error(response.data.error || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send test OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Voice Processing Functions
  const processVoiceFile = async () => {
    if (!voiceFile || !voicePhone) {
      toast.error('Please select a voice file and enter phone number');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('voiceFile', voiceFile);
      formData.append('phoneNumber', voicePhone);
      formData.append('messageId', `test_voice_${Date.now()}`);

      const response = await whatsappAPI.processVoiceNote({
        messageId: `test_voice_${Date.now()}`,
        phoneNumber: voicePhone,
        voiceFileUrl: URL.createObjectURL(voiceFile),
        voiceFileId: `voice_${Date.now()}`,
        durationSeconds: 30,
        fileSizeBytes: voiceFile.size
      });

      if (response.data.success) {
        toast.success('Voice processing initiated!');
      } else {
        toast.error(response.data.error || 'Failed to process voice');
      }
    } catch (error) {
      toast.error('Failed to process voice file');
    } finally {
      setIsLoading(false);
    }
  };

  // Payment Links Functions
  const createPaymentLink = async () => {
    if (!paymentData.orderId || !paymentData.phoneNumber || !paymentData.amount) {
      toast.error('Please fill all payment fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.createPaymentLink(paymentData);

      if (response.data.success) {
        toast.success('Payment link created successfully!');
      } else {
        toast.error(response.data.error || 'Failed to create payment link');
      }
    } catch (error) {
      toast.error('Failed to create payment link');
    } finally {
      setIsLoading(false);
    }
  };

  // AI Features Functions
  const analyzeText = async () => {
    if (!aiText || !aiPhone) {
      toast.error('Please enter text and phone number');
      return;
    }

    setIsLoading(true);
    try {
      const [sentimentResponse, translateResponse] = await Promise.all([
        whatsappAPI.analyzeSentiment({
          messageId: `test_ai_${Date.now()}`,
          phoneNumber: aiPhone,
          text: aiText
        }),
        whatsappAPI.autoTranslate({
          messageId: `test_translate_${Date.now()}`,
          phoneNumber: aiPhone,
          text: aiText
        })
      ]);

      setAiResults({
        sentiment: sentimentResponse.data?.data,
        translation: translateResponse.data?.data
      });

      toast.success('AI analysis completed!');
    } catch (error) {
      toast.error('Failed to analyze text');
    } finally {
      setIsLoading(false);
    }
  };

  // Ticketing Functions
  const createTicket = async () => {
    if (!ticketData.phoneNumber || !ticketData.subject || !ticketData.description) {
      toast.error('Please fill all ticket fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.createTicket({
        ...ticketData,
        messageId: `test_ticket_${Date.now()}`
      });

      if (response.data.success) {
        toast.success('Ticket created successfully!');
        setTicketData({
          phoneNumber: '',
          subject: '',
          description: '',
          ticketType: 'general',
          priority: 'medium'
        });
      } else {
        toast.error(response.data.error || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setIsLoading(false);
    }
  };

  // Groups Functions
  const createGroup = async () => {
    if (!groupData.groupName || !groupData.description) {
      toast.error('Please fill all group fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.createGroup(groupData);

      if (response.data.success) {
        toast.success('Group created successfully!');
        setGroupData({
          groupName: '',
          description: '',
          groupType: 'community',
          maxMembers: 256
        });
      } else {
        toast.error(response.data.error || 'Failed to create group');
      }
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'otp', label: 'OTP Verification', icon: '🔐' },
    { id: 'voice', label: 'Voice Processing', icon: '🎤' },
    { id: 'payment', label: 'Payment Links', icon: '💳' },
    { id: 'ai', label: 'AI Features', icon: '🤖' },
    { id: 'tickets', label: 'Advanced Ticketing', icon: '🎫' },
    { id: 'groups', label: 'Groups Automation', icon: '👥' },
    { id: 'analytics', label: 'Analytics', icon: '📊' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'otp':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">OTP Verification Testing</h3>
              <p className="text-blue-700 dark:text-blue-300 text-sm">Test WhatsApp OTP functionality for various verification types.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={otpPhone}
                    onChange={(e) => setOtpPhone(e.target.value)}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Verification Type</label>
                  <select
                    value={otpType}
                    onChange={(e) => setOtpType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="login">Login</option>
                    <option value="registration">Registration</option>
                    <option value="password_reset">Password Reset</option>
                    <option value="order_verification">Order Verification</option>
                    <option value="payment_verification">Payment Verification</option>
                  </select>
                </div>

                <button
                  onClick={sendTestOTP}
                  disabled={isLoading || !otpPhone}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send Test OTP'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Recent OTP Stats</h4>
                {stats.otp && stats.otp.length > 0 ? (
                  <div className="space-y-2">
                    {stats.otp.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-white">{stat.verification_type}:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">
                          {stat.total_sent} sent, {stat.verified} verified
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No OTP data available</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'voice':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">Voice Processing Testing</h3>
              <p className="text-purple-700 dark:text-purple-300 text-sm">Test AI-powered voice-to-text conversion for WhatsApp voice messages.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice File</label>
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => setVoiceFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={voicePhone}
                    onChange={(e) => setVoicePhone(e.target.value)}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <button
                  onClick={processVoiceFile}
                  disabled={isLoading || !voiceFile || !voicePhone}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Processing...' : 'Process Voice File'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Voice Processing Stats</h4>
                {stats.voice && stats.voice.length > 0 ? (
                  <div className="space-y-2">
                    {stats.voice.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-white">Processed:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{stat.total_processed || 0}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No voice processing data available</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-6">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Payment Links Testing</h3>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm">Test payment link generation via WhatsApp for multiple gateways.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Order ID</label>
                    <input
                      type="text"
                      value={paymentData.orderId}
                      onChange={(e) => setPaymentData({...paymentData, orderId: e.target.value})}
                      placeholder="12345"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Amount</label>
                    <input
                      type="number"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                      placeholder="1000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={paymentData.phoneNumber}
                    onChange={(e) => setPaymentData({...paymentData, phoneNumber: e.target.value})}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currency</label>
                    <select
                      value={paymentData.currency}
                      onChange={(e) => setPaymentData({...paymentData, currency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gateway</label>
                    <select
                      value={paymentData.gateway}
                      onChange={(e) => setPaymentData({...paymentData, gateway: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="razorpay">Razorpay</option>
                      <option value="cashfree">Cashfree</option>
                      <option value="phonepe">PhonePe</option>
                      <option value="paytm">Paytm</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={createPaymentLink}
                  disabled={isLoading || !paymentData.orderId || !paymentData.phoneNumber || !paymentData.amount}
                  className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Payment Link'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Payment Stats</h4>
                {stats.payment && stats.payment.length > 0 ? (
                  <div className="space-y-2">
                    {stats.payment.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-white">{stat.gateway}:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">
                          {stat.total_links || 0} links, {stat.paid || 0} paid
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No payment data available</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'ai':
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mb-2">AI Features Testing</h3>
              <p className="text-indigo-700 dark:text-indigo-300 text-sm">Test AI-powered sentiment analysis and auto-translation features.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text to Analyze</label>
                  <textarea
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="Enter text for sentiment analysis and translation..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={aiPhone}
                    onChange={(e) => setAiPhone(e.target.value)}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <button
                  onClick={analyzeText}
                  disabled={isLoading || !aiText || !aiPhone}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Analyzing...' : 'Analyze with AI'}
                </button>
              </div>

              <div className="space-y-4">
                {aiResults.sentiment && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Sentiment Analysis</h4>
                    <div className="text-sm space-y-1">
                      <div><span className="font-medium text-gray-900 dark:text-white">Sentiment:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.sentiment.sentiment_label}</span></div>
                      <div><span className="font-medium text-gray-900 dark:text-white">Score:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.sentiment.sentiment_score}</span></div>
                      <div><span className="font-medium text-gray-900 dark:text-white">Confidence:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.sentiment.confidence_score}</span></div>
                    </div>
                  </div>
                )}

                {aiResults.translation && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Translation</h4>
                    <div className="text-sm space-y-1">
                      <div><span className="font-medium text-gray-900 dark:text-white">Original:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.translation.original_text}</span></div>
                      <div><span className="font-medium text-gray-900 dark:text-white">Translated:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.translation.translated_text}</span></div>
                      <div><span className="font-medium text-gray-900 dark:text-white">Language:</span> <span className="text-gray-700 dark:text-gray-300">{aiResults.translation.target_language}</span></div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">AI Processing Stats</h4>
                  {stats.ai && stats.ai.length > 0 ? (
                    <div className="space-y-2">
                      {stats.ai.map((stat, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium text-gray-900 dark:text-white">{stat.feature_type}:</span>
                          <span className="ml-2 text-gray-600 dark:text-gray-400">{stat.total_processed || 0} processed</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No AI processing data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'tickets':
        return (
          <div className="space-y-6">
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">Advanced Ticketing Testing</h3>
              <p className="text-red-700 dark:text-red-300 text-sm">Test multi-agent support ticket creation and management.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={ticketData.phoneNumber}
                    onChange={(e) => setTicketData({...ticketData, phoneNumber: e.target.value})}
                    placeholder="+919876543210"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject</label>
                  <input
                    type="text"
                    value={ticketData.subject}
                    onChange={(e) => setTicketData({...ticketData, subject: e.target.value})}
                    placeholder="Support Request"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <textarea
                    value={ticketData.description}
                    onChange={(e) => setTicketData({...ticketData, description: e.target.value})}
                    placeholder="Describe your issue..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ticket Type</label>
                    <select
                      value={ticketData.ticketType}
                      onChange={(e) => setTicketData({...ticketData, ticketType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="general">General</option>
                      <option value="technical">Technical</option>
                      <option value="billing">Billing</option>
                      <option value="sales">Sales</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</label>
                    <select
                      value={ticketData.priority}
                      onChange={(e) => setTicketData({...ticketData, priority: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={createTicket}
                  disabled={isLoading || !ticketData.phoneNumber || !ticketData.subject || !ticketData.description}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Support Ticket'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Ticket Stats</h4>
                {stats.tickets && stats.tickets.length > 0 ? (
                  <div className="space-y-2">
                    {stats.tickets.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-white">{stat.ticket_type}:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">
                          {stat.total_tickets || 0} tickets, {stat.resolved || 0} resolved
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No ticket data available</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'groups':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">Groups Automation Testing</h3>
              <p className="text-green-700 dark:text-green-300 text-sm">Test WhatsApp groups creation and automation features.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Group Name</label>
                  <input
                    type="text"
                    value={groupData.groupName}
                    onChange={(e) => setGroupData({...groupData, groupName: e.target.value})}
                    placeholder="Customer Community"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <textarea
                    value={groupData.description}
                    onChange={(e) => setGroupData({...groupData, description: e.target.value})}
                    placeholder="Group description..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Group Type</label>
                    <select
                      value={groupData.groupType}
                      onChange={(e) => setGroupData({...groupData, groupType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="community">Community</option>
                      <option value="business">Business</option>
                      <option value="support">Support</option>
                      <option value="announcements">Announcements</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Max Members</label>
                    <input
                      type="number"
                      value={groupData.maxMembers}
                      onChange={(e) => setGroupData({...groupData, maxMembers: parseInt(e.target.value)})}
                      min="1"
                      max="256"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <button
                  onClick={createGroup}
                  disabled={isLoading || !groupData.groupName || !groupData.description}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create WhatsApp Group'}
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Groups Info</h4>
                <div className="text-sm space-y-2">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">Features Available:</span>
                    <ul className="ml-4 mt-1 text-gray-600 dark:text-gray-400">
                      <li>• Group creation and management</li>
                      <li>• Member management</li>
                      <li>• Event creation and reminders</li>
                      <li>• Survey and poll creation</li>
                      <li>• Automated messaging</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">WhatsApp Advanced Features Analytics</h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm">Comprehensive analytics for all WhatsApp advanced features.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🔐</span> OTP Analytics
                </h4>
                {stats.otp && stats.otp.length > 0 ? (
                  <div className="space-y-2">
                    {stats.otp.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">{stat.verification_type}</div>
                        <div className="text-gray-600 dark:text-gray-400">
                          Sent: {stat.total_sent || 0} | Verified: {stat.verified || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No OTP data available</p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🎤</span> Voice Processing
                </h4>
                {stats.voice && stats.voice.length > 0 ? (
                  <div className="space-y-2">
                    {stats.voice.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">Processed Files</div>
                        <div className="text-gray-600 dark:text-gray-400">Total: {stat.total_processed || 0}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No voice data available</p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">💳</span> Payment Links
                </h4>
                {stats.payment && stats.payment.length > 0 ? (
                  <div className="space-y-2">
                    {stats.payment.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">{stat.gateway}</div>
                        <div className="text-gray-600 dark:text-gray-400">
                          Links: {stat.total_links || 0} | Paid: {stat.paid || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No payment data available</p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🤖</span> AI Features
                </h4>
                {stats.ai && stats.ai.length > 0 ? (
                  <div className="space-y-2">
                    {stats.ai.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">{stat.feature_type}</div>
                        <div className="text-gray-600 dark:text-gray-400">Processed: {stat.total_processed || 0}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No AI data available</p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">🎫</span> Support Tickets
                </h4>
                {stats.tickets && stats.tickets.length > 0 ? (
                  <div className="space-y-2">
                    {stats.tickets.map((stat, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">{stat.ticket_type}</div>
                        <div className="text-gray-600 dark:text-gray-400">
                          Total: {stat.total_tickets || 0} | Resolved: {stat.resolved || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No ticket data available</p>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <span className="mr-2">👥</span> Groups
                </h4>
                <div className="text-sm">
                  <div className="font-medium text-gray-900 dark:text-white">Features Available</div>
                  <div className="text-gray-600 dark:text-gray-400 mt-1">
                    • Group creation<br/>
                    • Member management<br/>
                    • Event reminders<br/>
                    • Surveys & polls
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">WhatsApp Advanced Features</h1>
        <p className="text-gray-600 dark:text-gray-400">Test and manage advanced WhatsApp features</p>
      </div>

      {/* Main Container */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto scrollbar-hide px-4 sm:px-6" aria-label="Tabs">
            <div className="flex space-x-2 sm:space-x-8 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600 dark:text-green-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <span className="mr-1 sm:mr-2">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppAdvancedFeatures;
